import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Heading2 } from '../../ui/Typography/Heading';
import { Button } from '../../ui/Button/Button';
import { updateConfigOnboardingSettings } from '../../../redux/config/slice';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import { ImportFlow } from '../../Import/ImportFlow';
import { IMPORT_STEP_TEXTS } from '../../Import/importStepTexts';
import { IconDownload } from '../../ui/Icons';
import { downloadImportTemplate, exportRisksAndPoliciesExportFile } from '../../../redux/import/slice';
import { selectUpdateConfigLoading, selectConfigOnboardingImportDone } from '../../../redux/config/selectors';
import { selectExportRisksAndPoliciesFileLoading } from '../../../redux/import/selectors';
import { selectRisks } from '../../../redux/risks/selectors';
import { getRisks } from '../../../redux/risks/slice';
import { selectPolicies } from '../../../redux/policies/selectors';
import { getPolicies } from '../../../redux/policies/slice';
import { ONBOARDING_STEPS, ONBOARDING_SOURCE_TYPES } from '../../../consts/consts';
import {
  OnboardingHeaderGroup,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingContentTitle,
  OnboardingStepWrapper,
  OnboardingContentInfo,
  OnboardingContentGroupText,
  OnboardingContentGroupLink,
  OnboardingImportStepList,
  OnboardingImportStepItem,
  OnboardingImportStepNumber,
  OnboardingImportStepContent,
  OnboardingImportStepHeader,
  OnboardingImportStepTitle,
  OnboardingImportStepDescription,
  OnboardingImportStepUploaderWrapper
} from '../../ui/Layout/OnboardingLayout';

const ONBOARDING_IMPORT_HELP_CENTER_URL =
  'https://help.oqio.de/de/articles/13611129-umgang-mit-fehlern-beim-stammdaten-import?utm_source=onboarding&utm_medium=inproduct';
const ONBOARDING_IMPORT_GUIDE_URL =
  'https://help.oqio.de/de/articles/13611115-erster-stammdaten-import?utm_source=onboarding&utm_medium=inproduct';

export const OnboardingStepImportUploadComponent = ({
  updateConfigOnboardingSettings,
  updateConfigLoading,
  importDone,
  risks,
  policies,
  exportLoading,
  downloadImportTemplate,
  exportRisksAndPoliciesExportFile,
  getRisks,
  getPolicies
}) => {
  const hasData = risks?.length > 0 || policies?.length > 0;
  const [validationTriggered, setValidationTriggered] = useState(false);

  useEffect(() => {
    getRisks();
    getPolicies();
  }, []);
  const handleBack = () => {
    updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.DATA_SOURCE_SELECT });
  };

  const handleNext = () => {
    if (!importDone) {
      setValidationTriggered(true);
      return;
    }
    updateConfigOnboardingSettings({
      step: ONBOARDING_STEPS.PARTNER_USERS,
      dataSource: ONBOARDING_SOURCE_TYPES.IMPORT
    });
  };

  const handleDownloadTemplate = () => {
    if (hasData) {
      exportRisksAndPoliciesExportFile();
    } else {
      downloadImportTemplate();
    }
  };

  const handleImportSuccess = () => {
    updateConfigOnboardingSettings({ dataSource: ONBOARDING_SOURCE_TYPES.IMPORT, importDone: true });
  };

  return (
    <>
      <OnboardingNavbar loading={updateConfigLoading} />
      <OnboardingProgress />
      <OnboardingContent className="step-import-upload">
        <OnboardingStepWrapper>
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Import direkt in oqio</Heading2>
            </OnboardingContentTitle>
            <OnboardingContentInfo>
              <OnboardingContentGroupText>
                Importieren Sie hier Ihre Stammdaten, um zu starten. Fehlende Angaben können Sie später im Portal
                ergänzen. Orientieren Sie sich dabei an der Anleitung im Help Center.
              </OnboardingContentGroupText>
              <OnboardingContentGroupLink href={ONBOARDING_IMPORT_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                Zur Anleitung des Imports
              </OnboardingContentGroupLink>
            </OnboardingContentInfo>
          </OnboardingHeaderGroup>

          <OnboardingContentGroup className="step-import-upload content">
            <OnboardingImportStepList>
              <OnboardingImportStepItem>
                <OnboardingImportStepNumber>1</OnboardingImportStepNumber>
                <OnboardingImportStepContent>
                  <OnboardingImportStepHeader>
                    <OnboardingImportStepTitle>
                      {hasData
                        ? IMPORT_STEP_TEXTS.downloadSyncExport.title
                        : IMPORT_STEP_TEXTS.downloadNewTemplate.title}
                    </OnboardingImportStepTitle>{' '}
                    <Button
                      type="link"
                      icon={<IconDownload />}
                      loading={hasData && exportLoading}
                      onClick={handleDownloadTemplate}
                    >
                      {hasData
                        ? IMPORT_STEP_TEXTS.downloadSyncExport.title
                        : IMPORT_STEP_TEXTS.downloadNewTemplate.title}
                    </Button>
                  </OnboardingImportStepHeader>
                  <OnboardingImportStepDescription>
                    {hasData
                      ? IMPORT_STEP_TEXTS.downloadSyncExport.description
                      : IMPORT_STEP_TEXTS.downloadNewTemplate.description}
                  </OnboardingImportStepDescription>
                </OnboardingImportStepContent>
              </OnboardingImportStepItem>

              <OnboardingImportStepItem>
                <OnboardingImportStepNumber>2</OnboardingImportStepNumber>
                <OnboardingImportStepContent>
                  <OnboardingImportStepTitle>{IMPORT_STEP_TEXTS.fillTemplate.title}</OnboardingImportStepTitle>
                  <OnboardingImportStepDescription>
                    Öffnen Sie die Vorlage, erfassen Sie Ihre Stammdaten und orientieren Sie sich dabei an den
                    Erklärungen in der Datei.
                  </OnboardingImportStepDescription>
                  <OnboardingImportStepDescription>
                    Erfassen Sie jedes Gebäude als einzelnes Risiko in einer Zeile (pro Hausnummer), damit die
                    Immobilienwirtschaft Schäden pro Gebäude melden kann.
                  </OnboardingImportStepDescription>
                </OnboardingImportStepContent>
              </OnboardingImportStepItem>

              <OnboardingImportStepItem>
                <OnboardingImportStepNumber>3</OnboardingImportStepNumber>
                <OnboardingImportStepContent>
                  <OnboardingImportStepTitle>Ausgefüllte Datei hochladen</OnboardingImportStepTitle>
                  <OnboardingImportStepDescription>
                    Laden Sie die ausgefüllte Excel-Datei hoch und prüfen Sie Ihre Angaben, um die Stammdaten zu
                    importieren.
                  </OnboardingImportStepDescription>
                  <OnboardingImportStepUploaderWrapper>
                    <ImportFlow
                      onSuccess={handleImportSuccess}
                      hasError={validationTriggered && !importDone}
                      initialShowSuccess={importDone}
                      helpCenterUrl={ONBOARDING_IMPORT_HELP_CENTER_URL}
                    />
                  </OnboardingImportStepUploaderWrapper>
                </OnboardingImportStepContent>
              </OnboardingImportStepItem>
            </OnboardingImportStepList>
          </OnboardingContentGroup>
        </OnboardingStepWrapper>
        <OnboardingActions onBack={handleBack} onNext={handleNext} loading={updateConfigLoading} />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  updateConfigLoading: selectUpdateConfigLoading(state),
  importDone: selectConfigOnboardingImportDone(state),
  risks: selectRisks(state),
  policies: selectPolicies(state),
  exportLoading: selectExportRisksAndPoliciesFileLoading(state)
});

const mapDispatchToProps = {
  updateConfigOnboardingSettings,
  downloadImportTemplate,
  exportRisksAndPoliciesExportFile,
  getRisks,
  getPolicies
};

export const OnboardingStepImportUpload = connect(
  mapStateToProps,
  mapDispatchToProps
)(OnboardingStepImportUploadComponent);
